import { Cell, LabelSpacer, Row } from '@fs/zion-ui'
import React, { useCallback } from 'react'
import { useSlsStateContext, useSlsHighlightsContext, getNextUuid } from '@fs/zion-sls'
import { FormTextField, useZionFormContext } from '@fs/zion-form'
import { useTranslation } from 'react-i18next'
import useNamesExperienceOptIn from '../../../../hooks/useNamesExperienceOptIn'
import { handleInputBlur, handleInputFocus, prepareFieldForHighlights } from '../../shared/utils/highlightUtils'
import HighlightButton from '../../panels/shared/HighlightButton'

const EventFormInput = ({
  index,
  indexName,
  field,
  eventIndex,
  SuggestComponent,
  suggestFieldType,
  suggestLabelKey,
  suggestDefaultLabel,
  primaryEvent,
  splitScreenImageArk,
  onValidateFields,
}) => {
  const { id, type, displayLabel, editable, shared, added } = field
  const basePath = `events.${primaryEvent ? 'primary' : 'additional'}[${eventIndex}].${indexName}[${index}]`

  const { getValues, resetField, setValue } = useZionFormContext()
  const { slsTypes, images } = useSlsStateContext()
  const h = useSlsHighlightsContext()
  const [t] = useTranslation()
  const { isOptedIn } = useNamesExperienceOptIn()

  // Enhanced blur handler that triggers validation on both date and place fields
  const handleFieldBlur = useCallback(() => {
    // First, handle the highlight blur (existing behavior)
    handleInputBlur(h)

    // Then trigger validation on both date and place fields if callback provided
    if (onValidateFields) {
      onValidateFields()
    }
  }, [h, onValidateFields])

  // Handle highlight save and update form field with new shared field
  const handleHighlightSave = useCallback(
    (fieldName) => (data) => {
      // Update the field with the new shared field ID and value
      if (data?.newFieldId && data?.newFieldValue) {
        const currentFieldData = getValues(fieldName)
        let newFieldValue = data.newFieldValue

        if (!['DATE', 'PLACE'].includes(currentFieldData?.type)) {
          newFieldValue = {
            key: getNextUuid(),
            primaryText: data.newFieldValue,
            standard: null,
          }
        }

        resetField(fieldName, {
          defaultValue: {
            ...currentFieldData,
            id: data.newFieldId,
            value: newFieldValue,
            dispatches: data?.dispatches || [],
            shared: data?.selectedPersonIds?.length > 1, // Share the field if it's associated with multiple persons
          },
          keepDirty: false,
          keepErrors: false,
          keepTouched: false,
        })

        // Ensure form value is updated immediately so the suggest input reflects the new value
        setValue(`${fieldName}.value`, newFieldValue, {
          shouldDirty: true,
          shouldTouch: true,
        })
      }
    },
    [resetField, setValue, getValues]
  )

  if (type === suggestFieldType && SuggestComponent) {
    const Suggest = SuggestComponent
    const suggestTestId = `${indexName.slice(0, -1)}-field`
    return (
      <Row gutters="xxs" key={`sub-${id || index}`}>
        <Suggest
          key={id}
          name={`${basePath}.value`}
          label={t(suggestLabelKey, suggestDefaultLabel)}
          data-testid={`${suggestTestId}-${index}`}
          standardize
          onFocus={() => handleInputFocus(`${basePath}.id`, getValues, h)}
          onBlur={handleFieldBlur}
          onSuggestionSelected={() => {
            if (onValidateFields) {
              onValidateFields()
            }
          }}
          onClear={() => {
            if (onValidateFields) {
              onValidateFields()
            }
          }}
          disabled={!editable}
          helpMessage={
            isOptedIn && shared
              ? t('FormValidation.sharedField', 'This field is associated with multiple names.')
              : undefined
          }
          growX
          dense
        />
        <Cell>
          <LabelSpacer />
          <HighlightButton
            field={prepareFieldForHighlights(getValues, type, basePath, slsTypes, images)}
            splitScreenImageArk={splitScreenImageArk}
            buttonProps={{ 'data-testid': `${indexName}-highlight-button` }}
            linkName={`essential-information-${indexName}-highlight`}
            disabled={!editable}
            onSave={handleHighlightSave(`${basePath}`)}
            saveToSls={!added}
            growX
            iconOnly
          />
        </Cell>
      </Row>
    )
  }

  const textTestId = `${indexName.slice(0, -1)}-field`

  return (
    <Row gutters="xxs" key={`sub-${id || index}`}>
      <FormTextField
        key={id}
        name={`${basePath}.value`}
        label={displayLabel}
        data-testid={textTestId}
        onFocus={() => handleInputFocus(`${basePath}.id`, getValues, h)}
        onBlur={handleFieldBlur}
        disabled={!editable}
        helpMessage={
          isOptedIn && shared
            ? t('FormValidation.sharedField', 'This field is associated with multiple names.')
            : undefined
        }
        growX
        dense
      />
      <Cell>
        <LabelSpacer />
        <HighlightButton
          field={prepareFieldForHighlights(getValues, type, basePath, slsTypes, images)}
          splitScreenImageArk={splitScreenImageArk}
          buttonProps={{ 'data-testid': `${indexName}-highlight-button` }}
          linkName={`essential-information-${indexName}-highlight`}
          disabled={!editable}
          onSave={handleHighlightSave(`${basePath}`)}
          saveToSls={!added}
          growX
          iconOnly
        />
      </Cell>
    </Row>
  )
}

export default EventFormInput
